import React, { useCallback, useContext, useEffect, useState } from "react";
import PageContext from "../Common/PageContext";
import { ModalBase } from "./ModalBase";
import alertTypeEnum from "../../util/alertTypeEnum";
import { LoadingDialog } from "../Loading/LoadingDialog";
import modalSize from "../../util/modalSizeEnum";
import {
	isFeatureEnabledForLocationAndReceiveType,
	isFeatureEnabledForLocationReceiveTypeAndTransferReason,
} from "../../util/featureUtil";
import { getCustomerOrdersAsync } from "../../services/apiHelper";
import { reduceCustomerOrdersToFIFO } from "../../util/customerOrderUtil";
import { PrinterOutputType } from "../../util/printerEnums";
import { receiveType } from "../../util/receiveTypeMapping";
import { PageContextType } from "../Common/PageContextType";
import { Box, Checkbox, Grid2 as Grid, Typography, SelectChangeEvent, useTheme } from "@mui/material";
import PrinterSelect from "../Printer/PrinterSelect";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ErrorIcon from "@mui/icons-material/Error";
import { Receive, ReceiveLineItem } from "../../common/Receive";
import { feature } from "../../common/api/Models/FeatureConfiguration";
import { AllocatedCustomerOrder } from "../../common/CustomerOrder";
import { TransferReceiveVariance } from "../../common/Transfer";
import { getFreightCostHighWarning, isFreightCostHigh } from "../../util/freightUtil";
import { formatCurrency } from "../../util/currencyUtil";
import { AlertInformation } from "../AlertInformation/AlertInformation";

interface ModalReceiveFinaliseProps {
	isOpen: boolean;
	onFinaliseSubmit: (
		customerOrders: AllocatedCustomerOrder[],
		hasLabels: boolean,
		skipAllocation: boolean,
		skipLabelPrinting: boolean
	) => Promise<void>;
	onCancelFinalise: () => void;
	locationId: string;
	receive: Receive;
	originalReceive?: Receive;
	hasExistingCredit: boolean;
	isCreditRequestClaimed?: boolean;
	skipAllocation: boolean;
	hasLabels: boolean;
	variances?: {
		overReceivedItems: TransferReceiveVariance[];
		underReceivedItems: TransferReceiveVariance[];
	};
	pendingReceive?: boolean;
	transferReason?: string;
	showInvoiceTotals?: boolean;
	showFreightCost?: boolean;
}

const ModalReceiveFinalise: React.FC<ModalReceiveFinaliseProps> = ({
	isOpen,
	onFinaliseSubmit,
	onCancelFinalise,
	locationId,
	receive,
	originalReceive,
	hasExistingCredit,
	isCreditRequestClaimed = false,
	skipAllocation,
	hasLabels,
	variances,
	pendingReceive,
	transferReason,
	showInvoiceTotals = false,
	showFreightCost = false,
}) => {
	const theme = useTheme();
	const [pageContext, updatePageContextAsync] = useContext(PageContext) as PageContextType;
	const [allocatedCustomerOrders, setAllocatedCustomerOrders] = useState<AllocatedCustomerOrder[]>([]);
	const [numberCustomerOrders, setNumberCustomerOrders] = useState<number>(0);
	const [customerOrdersLoading, setCustomerOrdersLoading] = useState<boolean>(true);
	const [modalTitle, setModalTitle] = useState<string>("Submit Receive?");
	const [modalMessage, setModalMessage] = useState<string>("You are about to submit this receive");
	const [hasPrinters, setHasPrinters] = useState<boolean>(false);
	const [hasLabelPrinters, setHasLabelPrinters] = useState<boolean>(false);
	const [skipAutoOrderAllocation, setSkipAutoOrderAllocation] = useState<boolean>(false);
	const [skipProductLabelPrinting, setSkipProductLabelPrinting] = useState<boolean>(false);

	const handlePrinterChange = async (e: SelectChangeEvent<string>) => {
		updatePageContextAsync({ selectedPrinterId: e.target.value as string });
	};

	const handleLabelPrinterChange = async (e: SelectChangeEvent<string>) => {
		updatePageContextAsync({ selectedLabelPrinterId: e.target.value as string });
	};

	const {
		featureConfiguration: { customerOrderAllocationAndAdvancement, skipCustomerOrderAllocation },
		printers,
		selectedPrinterId,
		selectedLabelPrinterId,
	}: {
		featureConfiguration: {
			customerOrderAllocationAndAdvancement: feature;
			skipCustomerOrderAllocation: feature;
		};
		printers: any[];
		selectedPrinterId?: string;
		selectedLabelPrinterId?: string;
	} = pageContext;

	const getCustomerOrders = useCallback(async () => {
		if (!locationId) return;
		if (
			skipAllocation ||
			!isFeatureEnabledForLocationReceiveTypeAndTransferReason(
				locationId,
				customerOrderAllocationAndAdvancement,
				receive.receiveType,
				transferReason
			)
		) {
			setCustomerOrdersLoading(false);
			return;
		}

		const lineItems = receive.receiveLineItems.map((item: ReceiveLineItem) => ({
			sku: item.sku,
			quantity: item.receivedQuantity - item.quantityAlreadyReceived,
		}));

		const response = await getCustomerOrdersAsync(lineItems, locationId);
		setCustomerOrdersLoading(false);
		setAllocatedCustomerOrders(reduceCustomerOrdersToFIFO(receive, response.orders, originalReceive));
	}, [
		locationId,
		skipAllocation,
		customerOrderAllocationAndAdvancement,
		receive,
		transferReason,
		originalReceive,
	]);
	const allowSkipCustomerOrderAllocation = isFeatureEnabledForLocationAndReceiveType(
		locationId,
		skipCustomerOrderAllocation,
		receive.receiveType
	);

	useEffect(() => {
		// allocated customer orders are a combination of order and line item
		// count the number of orders
		const orderCount = new Set(allocatedCustomerOrders.map((obj) => obj.OrderId)).size;
		setNumberCustomerOrders(orderCount);
	}, [allocatedCustomerOrders]);

	useEffect(() => {
		getCustomerOrders();
	}, [getCustomerOrders]);

	useEffect(() => {
		setHasPrinters(printers && printers.length > 0 && printers.some((p) => p.outputType === PrinterOutputType.A4));
		setHasLabelPrinters(
			printers && printers.length > 0 && printers.some((p) => p.outputType === PrinterOutputType.Zebra)
		);
	}, [printers]);

	useEffect(() => {
		switch (receive.receiveType.toLowerCase()) {
			case receiveType.manual.toLowerCase(): {
				setModalTitle("Submit Receive?");
				allocatedCustomerOrders.length > 0
					? setModalMessage("You are about to submit this receive for review and allocate items to:")
					: setModalMessage("You are about to submit this receive for review");
				break;
			}
			case receiveType.ediLikeAManual.toLowerCase():
			case receiveType.wasEdiLikeAManual.toLowerCase(): {
				setModalTitle(
					(variances?.underReceivedItems ?? []).length > 0 && pendingReceive
						? "Submit Pending Receive"
						: "Finalise receive?"
				);
				allocatedCustomerOrders.length > 0
					? setModalMessage("You are about to finalise this receive and allocate items to:")
					: setModalMessage("You are about to finalise this receive");
				break;
			}
			case receiveType.transfer.toLowerCase(): {
				setModalTitle(
					(variances?.underReceivedItems ?? []).length > 0 && pendingReceive
						? "Submit Pending Receive"
						: "Finalise receive?"
				);
				setModalMessage("You are about to submit this receive");
				break;
			}
			case receiveType.edi.toLowerCase(): {
				setModalTitle(`Finalise receive${hasExistingCredit && !isCreditRequestClaimed ? " and credit request" : ""}?`);
				const message =
					hasExistingCredit && !isCreditRequestClaimed
						? "You are about to finalise this receive and credit request"
						: hasExistingCredit && isCreditRequestClaimed
						? "You are about to finalise this receive. Linked Credit Request is already claimed"
						: "You are about to finalise this receive";
				allocatedCustomerOrders.length > 0
					? setModalMessage(`${message}. Items will be allocated to:`)
					: setModalMessage(message);
				break;
			}
			default: {
				break;
			}
		}
	}, [receive, allocatedCustomerOrders, hasExistingCredit, variances, pendingReceive, isCreditRequestClaimed]);

	const lineTotalCostEx = receive.receiveLineItems.reduce(
		(total: number, item: ReceiveLineItem) => total + (item.lineTotalCostEx ?? 0),
		0
	);
	const lineTotalCostInc = receive.receiveLineItems.reduce(
		(total: number, item: ReceiveLineItem) => total + (item.lineTotalCostInc ?? 0),
		0
	);
	const totalCostInc = lineTotalCostInc + (receive.totalInvoicedFreightInc ?? 0);
	const freightCostHigh = showFreightCost && isFreightCostHigh(receive.totalInvoicedFreightEx, lineTotalCostEx);

	return (
		<ModalBase
			isOpen={isOpen}
			message={""}
			primaryButtonName="Submit"
			secondaryButtonName="Go back"
			title={modalTitle}
			onPrimaryButtonClick={() =>
				onFinaliseSubmit(allocatedCustomerOrders, hasLabels, skipAutoOrderAllocation, skipProductLabelPrinting)
			}
			onSecondaryButtonClick={onCancelFinalise}
			onClose={onCancelFinalise}
			alertType={alertTypeEnum.INFO}
			size={modalSize.FULLSCREEN}
			children={
				<>
					<LoadingDialog message={"Checking for customer orders"} isOpen={customerOrdersLoading} />
					<Grid container direction="column" spacing={3} justifyContent="center">
						<Grid size="grow">
							<Grid container size={12}>
								<Grid size="grow">
									<Typography variant="body1">{modalMessage}</Typography>
								</Grid>
							</Grid>
						</Grid>
						{showInvoiceTotals && (
							<Grid size="grow">
								<Box>
									<Typography variant="body2" color="textSecondary" marginBottom={theme.spacing(0.5)}>
										Invoice Totals
									</Typography>
									{showFreightCost && (
										<Box display="flex" alignItems="center" marginBottom={theme.spacing(0.5)}>
											{!freightCostHigh && (
												<LocalShippingIcon
													sx={{ color: theme.palette.custom.jbColours.darkOrange, fontSize: 20, marginRight: 0.5 }}
												/>
											)}
											<Typography variant="body2">
												Freight Cost Ex:{" "}
												<span
													style={freightCostHigh ? { color: theme.palette.custom.jbColours.darkOrange } : undefined}
												>
													{formatCurrency(receive.totalInvoicedFreightEx)}
												</span>
											</Typography>
											{freightCostHigh && (
												<ErrorIcon
													sx={{ color: theme.palette.custom.jbColours.darkOrange, fontSize: 20, marginLeft: 0.5 }}
												/>
											)}
										</Box>
									)}
									<Typography variant="body2" marginBottom={theme.spacing(0.5)}>
										Line Total Cost Ex: {formatCurrency(lineTotalCostEx)}
									</Typography>
									<Typography variant="body2" marginBottom={theme.spacing(0.5)}>
										Total Cost Inc: {formatCurrency(totalCostInc)}{" "}
										{showFreightCost && (
											<Typography component="span" color="textSecondary" sx={{ fontSize: "0.77rem" }}>
												(incl freight charges)
											</Typography>
										)}
									</Typography>
									{freightCostHigh && (
										<Box style={{ marginTop: theme.spacing(2) }}>
											<AlertInformation severity="warning" message={getFreightCostHighWarning(receive.receiveType)} />
										</Box>
									)}
								</Box>
							</Grid>
						)}
						{allocatedCustomerOrders.length > 0 && (
							<>
								<Grid size="grow">
									<Grid container size={12}>
										<Grid size={12}>
											<Typography variant="body1" align="center">
												<strong>
													{numberCustomerOrders} customer order{numberCustomerOrders > 1 ? "s" : ""}
												</strong>
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Grid size="grow">
									<Grid container>
										<Grid size={2}>
											<PrintIcon fontSize="large" />
										</Grid>
										<Grid size={10}>
											<Grid container>
												<Grid size="grow">
													{!hasPrinters && (
														<Typography variant="body2">
															There are customers orders on this receive however the app has been unable to load
															printers. Orders will still be allocated upon finalising - please contact IT Support to
															add a printer.
														</Typography>
													)}
													{hasPrinters && (
														<PrinterSelect
															title={`Review your printer selection, and change if needed.`}
															handleChange={handlePrinterChange}
															selectedPrinterId={selectedPrinterId ?? ""}
															printers={printers}
															disabled={skipAutoOrderAllocation}
														/>
													)}
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</>
						)}
						{hasLabels && hasLabelPrinters && (
							<>
								<Grid size="grow">
									<Grid container size={12}>
										<Grid size={2}>
											<PrintIcon fontSize="large" />
										</Grid>
										<Grid size={10}>
											<Grid container size={12}>
												<Grid size="grow">
													{hasLabelPrinters && (
														<PrinterSelect
															title={`Product Labels will be printed to the following printer`}
															handleChange={handleLabelPrinterChange}
															selectedPrinterId={selectedLabelPrinterId ?? ""}
															printers={printers}
															outputType={PrinterOutputType.Zebra}
															disabled={skipProductLabelPrinting}
														/>
													)}
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</>
						)}
						{hasLabels && hasLabelPrinters && (
							<Grid size="grow">
								<Grid container size={12}>
									<Grid size={2}>
										<Checkbox
											checked={skipProductLabelPrinting}
											onChange={(e) => setSkipProductLabelPrinting(e.target.checked)}
											color="primary"
											style={{ padding: 0, paddingLeft: 5.5, color: "black" }}
										/>
									</Grid>
									<Grid size={10}>
										<Grid container size={12}>
											<Grid size="grow">
												<Typography variant="body1">Skip product label printing.</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
						{allowSkipCustomerOrderAllocation && allocatedCustomerOrders.length > 0 && (
							<Grid size="grow">
								<Grid container size={12}>
									<Grid size={2}>
										<Checkbox
											checked={skipAutoOrderAllocation}
											onChange={(e) => setSkipAutoOrderAllocation(e.target.checked)}
											color="primary"
											style={{ padding: 0, paddingLeft: 5.5, color: "black" }}
										/>
									</Grid>
									<Grid size={10}>
										<Grid container size={12}>
											<Grid size="grow">
												<Typography variant="body1">Skip automatic customer order allocation.</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</>
			}
		/>
	);
};

export default ModalReceiveFinalise;
